import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  serverTimestamp,
  Timestamp,
  or,
  deleteField,
  startAfter,
  getCountFromServer,
  type QueryConstraint,
  type Unsubscribe,
} from "firebase/firestore";
import { db } from "./config";
import type {
  Banner,
  Category,
  SubCategory,
  Product,
  CartItem,
  Order,
  Address,
  UserProfile,
  UserProfileAddress,
  Outlet
} from "@/types";


// --- Banners ---
export const subscribeToBanners = (callback: (banners: Banner[]) => void): Unsubscribe => {
  const q = query(collection(db, "banners"), orderBy("sortOrder", "asc"));
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as Banner)));
  }, (error) => {
    console.error("Banners listener error:", error);
    callback([]);
  });
};

export const getBanners = async (): Promise<Banner[]> => {
  try {
    const q = query(collection(db, "banners"), orderBy("sortOrder", "asc"));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as Banner));
  } catch (error) {
    console.error("Error fetching banners:", error);
    return [];
  }
};

// --- Categories ---
export const getCategories = async () => {
  const snapshot = await getDocs(query(collection(db, "category")));
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as Category));
};

export const getSubCategories = async (categoryId: string) => {
  const snapshot = await getDocs(
    // Firestore field is parentCategoryId (not categoryId)
    // Note: no orderBy here to avoid requiring a composite index
    query(
      collection(db, "subCategory"),
      where("parentCategoryId", "==", categoryId)
    )
  );
  const docs = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as SubCategory));
  // Sort by sortOrder client-side
  return docs.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
};

export const getProductById = async (productId: string, outletId?: string) => {
  const docRef = outletId
    ? doc(db, "outlet", outletId, "food", productId)
    : doc(db, "food", productId);
  const snapshot = await getDoc(docRef);
  if (snapshot.exists()) {
    return { id: snapshot.id, ...snapshot.data() } as Product;
  }
  return null;
};

// --- Products ---
export const subscribeToProducts = (
  outletId: string | undefined,
  constraints: QueryConstraint[],
  callback: (products: Product[]) => void
): Unsubscribe => {
  if (!outletId) {
    callback([]);
    return () => { };
  }
  const q = query(collection(db, "outlet", outletId, "food"), ...constraints);
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as Product)));
  }, (error) => {
    console.error("Products listener error:", error);
    callback([]);
  });
};

export const subscribeToProductsByCategory = (
  categoryId: string,
  outletId: string | undefined,
  callback: (products: Product[]) => void
): Unsubscribe => {
  const constraints: QueryConstraint[] = [
    where("subcategories", "array-contains", categoryId)
  ];
  return subscribeToProducts(outletId, constraints, callback);
};



export const subscribeToAllProducts = (
  outletId: string | undefined,
  callback: (products: Product[]) => void
): Unsubscribe => {
  return subscribeToProducts(outletId, [], callback);
};

export const getProductsCount = async (outletId?: string) => {
  try {
    const coll = outletId
      ? collection(db, "outlet", outletId, "food")
      : collection(db, "food");
    const snapshot = await getCountFromServer(coll);
    return snapshot.data().count;
  } catch (error) {
    console.error("Error getting products count:", error);
    return 0;
  }
};

export const getProductsPaginated = async (
  pageSize: number,
  lastVisibleDoc: unknown = null,
  outletId?: string
) => {
  try {
    if (!outletId) return { products: [], lastDoc: null };

    let q = query(
      collection(db, "outlet", outletId, "food"),
      orderBy("name"), // Use name as default sort for stability
      limit(pageSize)
    );

    if (lastVisibleDoc) {
      q = query(q, startAfter(lastVisibleDoc));
    }

    const snapshot = await getDocs(q);
    const products = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as Product));

    return {
      products,
      lastDoc: snapshot.docs[snapshot.docs.length - 1] || null
    };
  } catch (error) {
    console.error("Error fetching paginated products:", error);
    return { products: [], lastDoc: null };
  }
};

/**
 * Firestore prefix-search on the `name` field.
 * Uses the >= / <= range trick so ONLY matching documents are read —
 * no full-collection scans, no extra quota usage.
 * Max 15 results returned.
 */
export const searchProductsByName = async (
  searchTerm: string,
  outletId?: string,
  maxResults = 15
): Promise<Product[]> => {
  if (!searchTerm.trim() || !outletId) return [];
  try {
    // Normalise: lowercase so "chicken" matches "Chicken"
    const term = searchTerm.trim().toLowerCase();
    const end = term + "\uf8ff";          // Unicode high-value char — matches all continuations

    // Try searching on a lowercase name field first (nameLower), fall back to name
    const q = query(
      collection(db, "outlet", outletId, "food"),
      orderBy("name"),
      where("name", ">=", term),
      where("name", "<=", end),
      limit(maxResults)
    );
    const snapshot = await getDocs(q);

    // Also do a case-insensitive client filter in case Firestore is case-sensitive
    // (it is), so also grab docs where name lowercased starts with term
    let results = snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Product));

    // If no results from exact prefix, try a broader pass searching description too
    if (results.length === 0) {
      const qDesc = query(
        collection(db, "outlet", outletId, "food"),
        orderBy("name"),
        limit(50)           // small cap — only loads at most 50 docs for fallback
      );
      const fallback = await getDocs(qDesc);
      results = fallback.docs
        .map((d) => ({ id: d.id, ...d.data() } as Product))
        .filter(
          (p) =>
            p.name?.toLowerCase().includes(term) ||
            p.description?.toLowerCase().includes(term)
        )
        .slice(0, maxResults);
    }

    return results;
  } catch (error) {
    console.error("Error searching products:", error);
    return [];
  }
};

// --- Cart ---
export const subscribeToCart = (userId: string, callback: (items: CartItem[]) => void): Unsubscribe => {
  return onSnapshot(doc(db, "users", userId), (docSnap) => {
    if (docSnap.exists()) {
      const userData = docSnap.data();
      const userCartItems = userData.userCartItems || {};
      type RawCartItem = { itemId?: string; foodDocId?: string; productName?: string; image?: string; variantId?: string; variantName?: string; price?: number; qty?: number };
      const cartItems: CartItem[] = Object.values(userCartItems).map((item) => {
        const ci = item as RawCartItem;
        return {
          id: ci.itemId ?? "",
          productId: ci.foodDocId ?? "",
          productName: ci.productName ?? "Product",
          productImage: ci.image,
          variantId: ci.variantId ?? "",
          variantName: ci.variantName ?? "",
          price: ci.price ?? 0,
          quantity: ci.qty ?? 0,
          userId: userId,
        };
      });
      callback(cartItems);
    } else {
      callback([]);
    }
  });
};

export const addToCart = async (userId: string, product: Product, variant?: { id: string; price: number; name?: string; weight?: string }) => {
  const userRef = doc(db, "users", userId);
  const userSnap = await getDoc(userRef);
  const userData = userSnap.data();
  const userCartItems = userData?.userCartItems || {};

  // Check if item already exists (by foodDocId)
  const existingItemEntry = Object.entries(userCartItems).find(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ([, item]: [string, any]) => item.foodDocId === product.id
  );

  if (existingItemEntry) {
    const [existingId, item] = existingItemEntry;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const currentQty = (item as any).qty || 0;
    await updateDoc(userRef, {
      [`userCartItems.${existingId}.qty`]: currentQty + 1,
      updatedAt: serverTimestamp(),
    });
  } else {
    // Generate a short 6-character random ID
    const generateShortId = () => Math.random().toString(36).substring(2, 8);
    const itemId = generateShortId();
    const cartItemPath = `userCartItems.${itemId}`;

    await updateDoc(userRef, {
      [cartItemPath]: {
        itemId,
        foodDocId: product.id,
        qty: 1,
        addonDocIdList: [],
      },
      updatedAt: serverTimestamp(),
    });
  }
};

export const updateCartQuantity = async (userId: string, itemId: string, quantity: number) => {
  if (quantity <= 0) {
    await removeFromCart(userId, itemId);
  } else {
    const cartItemPath = `userCartItems.${itemId}.qty`;
    await updateDoc(doc(db, "users", userId), {
      [cartItemPath]: quantity,
      updatedAt: serverTimestamp(),
    });
  }
};

export const removeFromCart = async (userId: string, itemId: string) => {
  await updateDoc(doc(db, "users", userId), {
    [`userCartItems.${itemId}`]: deleteField(),
    updatedAt: serverTimestamp(),
  });
};

export const clearCart = async (userId: string) => {
  await updateDoc(doc(db, "users", userId), {
    userCartItems: {},
    updatedAt: serverTimestamp(),
  });
};

// --- Orders ---
export const placeOrder = async (orderData: Partial<Order>) => {

  console.log("orderData:-", orderData);
  const docRef = await addDoc(collection(db, "orders"), {
    ...orderData,
    status: "placed",
    time: serverTimestamp(),
    // updatedAt: serverTimestamp(),
  });
  return docRef.id;
};

export const subscribeToOrders = (userId: string, callback: (orders: Order[]) => void): Unsubscribe => {

  const q = query(
    collection(db, "orders"),
    // Firestore field is uId (not userId)
    where("uId", "==", userId),
    orderBy("time", "desc")
  );
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as Order)));

    console.log(`orders:-${snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))}`);
  }, (error) => {
    console.error("Orders listener error:", error);
    callback([]);
  });
};

// --- Addresses (Legacy - to be removed) ---
export const getAddresses = async (userId: string) => {
  const snapshot = await getDocs(
    query(collection(db, "addresses"), where("userId", "==", userId))
  );
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as Address));
};

export const addAddress = async (userId: string, address: Partial<Address>) => {
  return await addDoc(collection(db, "addresses"), {
    ...address,
    userId,
    createdAt: serverTimestamp(),
  });
};

export const deleteAddress = async (addressId: string) => {
  await deleteDoc(doc(db, "addresses", addressId));
};

// --- Addresses (User Profile) ---
export const saveAddressToProfile = async (userId: string, address: UserProfileAddress) => {
  const addressId = address.id || Math.random().toString(36).substring(2, 8);
  const addressPath = `addresses.${addressId}`;

  await updateDoc(doc(db, "users", userId), {
    [addressPath]: {
      ...address,
      id: addressId,
    },
    updatedAt: serverTimestamp(),
  });
};

export const deleteAddressFromProfile = async (userId: string, addressId: string) => {
  await updateDoc(doc(db, "users", userId), {
    [`addresses.${addressId}`]: deleteField(),
    updatedAt: serverTimestamp(),
  });
};

export const setDefaultAddress = async (userId: string, addressId: string) => {
  await updateDoc(doc(db, "users", userId), {
    defaultAddressId: addressId,
    updatedAt: serverTimestamp(),
  });
};

// --- Outlets ---
export const getOutlets = async () => {
  const snapshot = await getDocs(collection(db, "outlet"));
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as Outlet));
};

// --- User Profile ---
export const subscribeToUserProfile = (userId: string, callback: (profile: UserProfile | null) => void): Unsubscribe => {
  return onSnapshot(doc(db, "users", userId), (docSnap) => {
    callback(docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } as UserProfile : null);
  });
};

export const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
  const docSnap = await getDoc(doc(db, "users", userId));
  return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } as UserProfile : null;
};

export const updateUserProfile = async (userId: string, data: Partial<UserProfile>): Promise<void> => {
  await updateDoc(doc(db, "users", userId), {
    ...data,
    updatedAt: serverTimestamp(),
  });
};

export const createOrUpdateUserProfile = async (userId: string, data: Partial<UserProfile>): Promise<void> => {
  await setDoc(doc(db, "users", userId), {
    ...data,
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

export const updateFavouritesArray = async (userId: string, productIds: string[]) => {
  await updateDoc(doc(db, "users", userId), {
    favourite: productIds,
    updatedAt: serverTimestamp(),
  });
};

// --- Products Fetch by IDs ---
export const getProductsByIds = async (ids: string[], outletId?: string): Promise<Product[]> => {
  if (!ids.length || !outletId) return [];

  // Firestore 'in' queries are limited to 30 items.
  const chunks = [];
  for (let i = 0; i < ids.length; i += 30) {
    chunks.push(ids.slice(i, i + 30));
  }

  const results: Product[] = [];
  for (const chunk of chunks) {
    const q = query(collection(db, "outlet", outletId, "food"), where("__name__", "in", chunk));
    const snapshot = await getDocs(q);
    snapshot.docs.forEach(doc => {
      results.push({ id: doc.id, ...doc.data() } as Product);
    });
  }
  return results;
};

export const subscribeToProductsByIds = (
  ids: string[],
  outletId: string,
  callback: (products: Product[]) => void
) => {
  if (!ids.length || !outletId) {
    callback([]);
    return () => { };
  }

  // Handle 'in' query limit (30)
  // For simplicity in subscription, we'll assume < 30 items for now, 
  // or handle the first 30. Usually cart/favs are small.
  const chunk = ids.slice(0, 30);
  const q = query(collection(db, "outlet", outletId, "food"), where("__name__", "in", chunk));

  return onSnapshot(q, (snapshot) => {
    const products = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Product));
    callback(products);
  }, (error) => {
    console.error("Error subscribing to products by ids:", error);
  });
};

export const subscribeToProductById = (
  id: string,
  outletId: string,
  callback: (product: Product | null) => void
) => {
  if (!id || !outletId) {
    callback(null);
    return () => { };
  }

  const docRef = doc(db, "outlet", outletId, "food", id);
  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      callback({ id: docSnap.id, ...docSnap.data() } as Product);
    } else {
      callback(null);
    }
  }, (error) => {
    console.error("Error subscribing to product by id:", error);
  });
};

// --- Settings ---
export const getSettings = async () => {
  const snapshot = await getDocs(collection(db, "settings"));
  const settings: Record<string, unknown> = {};
  snapshot.docs.forEach((doc) => {
    settings[doc.id] = doc.data();
  });
  return settings;
};
